import { Head, Link, useForm } from "@inertiajs/react";
import { ArrowLeft, Save } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TextareaWithCounter } from "@/components/ui/textarea-with-counter";
import AppLayout from "@/layouts/app-layout";
import { suppliersPath } from "@/routes";
import type { BreadcrumbItem, Supplier } from "@/types";

interface SupplierNewProps {
  supplier: Supplier;
  categories: { id: number; name: string }[];
}

export default function SupplierNew({
  supplier,
  categories,
}: SupplierNewProps) {
  const { data, setData, post, processing, errors } = useForm({
    name: supplier.name ?? "",
    tax_id: supplier.tax_id ?? "",
    supplier_type: supplier.supplier_type ?? "",
    trade_name: supplier.trade_name ?? "",
    email: supplier.email ?? "",
    phone: supplier.phone ?? "",
    category_id: supplier.category_id?.toString() ?? "",
    notes: supplier.notes ?? "",
  });

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Proveedores",
      href: suppliersPath(),
    },
    {
      title: "Nuevo Proveedor",
      href: "#",
    },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post(suppliersPath());
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Nuevo Proveedor" />
      <div className="flex h-full flex-1 flex-col gap-6 p-4">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href={suppliersPath()}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Nuevo Proveedor</h1>
            <p className="text-muted-foreground">
              Crea un nuevo proveedor en el sistema
            </p>
          </div>
        </div>

        {/* Form */}
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Información del Proveedor</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Razón Social *</Label>
                <Input
                  id="name"
                  value={data.name}
                  onChange={(e) => setData("name", e.target.value)}
                  placeholder="Ingrese la razón social del proveedor"
                  disabled={processing}
                />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="tax_id">CUIT *</Label>
                <Input
                  id="tax_id"
                  value={data.tax_id}
                  onChange={(e) => setData("tax_id", e.target.value)}
                  placeholder="30-12345678-9"
                  disabled={processing}
                />
                {errors.tax_id && (
                  <p className="text-sm text-red-500">{errors.tax_id}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="supplier_type">Tipo de Proveedor</Label>
                <Select
                  value={data.supplier_type || undefined}
                  onValueChange={(value) => setData("supplier_type", value)}
                  disabled={processing}
                >
                  <SelectTrigger id="supplier_type">
                    <SelectValue placeholder="Seleccione el tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="distribuidor">Distribuidor</SelectItem>
                    <SelectItem value="fabricante">Fabricante</SelectItem>
                  </SelectContent>
                </Select>
                {errors.supplier_type && (
                  <p className="text-sm text-red-500">{errors.supplier_type}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="trade_name">Nombre Comercial</Label>
                <Input
                  id="trade_name"
                  value={data.trade_name}
                  onChange={(e) => setData("trade_name", e.target.value)}
                  placeholder="Nombre comercial del proveedor"
                  disabled={processing}
                />
                {errors.trade_name && (
                  <p className="text-sm text-red-500">{errors.trade_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={data.email}
                  onChange={(e) => setData("email", e.target.value)}
                  placeholder="email@ejemplo.com"
                  disabled={processing}
                />
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  value={data.phone}
                  onChange={(e) => setData("phone", e.target.value)}
                  placeholder="+54 11 1234-5678"
                  disabled={processing}
                />
                {errors.phone && (
                  <p className="text-sm text-red-500">{errors.phone}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="category_id">Categoría</Label>
                <Select
                  value={data.category_id || undefined}
                  onValueChange={(value) => setData("category_id", value)}
                  disabled={processing}
                >
                  <SelectTrigger id="category_id">
                    <SelectValue placeholder="Seleccione una categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem
                        key={category.id}
                        value={category.id.toString()}
                      >
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.category_id && (
                  <p className="text-sm text-red-500">{errors.category_id}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notas (opcional)</Label>
                <TextareaWithCounter
                  id="notes"
                  value={data.notes}
                  onChange={(e) => setData("notes", e.target.value)}
                  placeholder="Notas adicionales sobre el proveedor"
                  disabled={processing}
                  rows={4}
                  maxLength={500}
                  showCharacterCount={true}
                />
                {errors.notes && (
                  <p className="text-sm text-red-500">{errors.notes}</p>
                )}
              </div>

              <div className="flex items-center gap-4">
                <Button type="submit" disabled={processing}>
                  <Save className="h-4 w-4 mr-2" />
                  {processing ? "Creando..." : "Crear Proveedor"}
                </Button>
                <Button type="button" variant="outline" asChild>
                  <Link href={suppliersPath()}>Cancelar</Link>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
